import * as React from 'react';

import {
  enumScaleConnectionType,
  enumScaleFormatType,
  enumScaleMeasurementMode,
  type ScaleConnectionType,
  type ScaleMeasurementMode,
} from '@rematter/frontend-lib-api-client';
import {
  ContentSection,
  DEFAULT_WEIGHT_UNITS,
  LocalizedDate,
  Select,
  TextField,
} from '@rematter/ui-web';
import { cn } from '@rematter/ui-web/lib';
import { useAtomValue, useSetAtom } from 'jotai';
import { selectAtom } from 'jotai/utils';
import isEqual from 'lodash/isEqual';

import { camerasListQueryAtom } from '../../../_graphql/queryCamerasList';
import { $scaleForm, ScaleFormState } from '../../../_state/scaleForm.state';
import { useSettingsLocation } from '../../../_state/settingsModule.state';
import { useTranslations } from '../../../i18n/useTranslations';
import { ScaleDetailRecord } from '../../../types/sidecar';

type ScaleFormFieldsProps = {
  scale?: ScaleDetailRecord | null;
  className?: string;
};

const scaleFormSelector = (state: ScaleFormState) => ({
  name: state.name,
  connectionType: state.connectionType,
  scaleFormatType: state.scaleFormatType,
  measurementMode: state.measurementMode,
  onDemandString: state.onDemandString,
  unitOfMeasurement: state.unitOfMeasurement,
  tcpHost: state.tcpHost,
  tcpPort: state.tcpPort,
  serialPortPath: state.serialPortPath,
  baudRate: state.baudRate,
  dataBits: state.dataBits,
  stopBits: state.stopBits,
  parity: state.parity,
  cameraId: state.cameraId,
});

export const ScaleFormFields = ({ scale, className }: ScaleFormFieldsProps) => {
  const t = useTranslations();
  const [, setLocation] = useSettingsLocation();

  const selectedFormAtom = React.useMemo(
    () => selectAtom($scaleForm, scaleFormSelector, isEqual),
    [],
  );

  const form = useAtomValue(selectedFormAtom);
  const updateForm = useSetAtom($scaleForm);

  const handleFieldChange = React.useCallback(
    <K extends keyof ScaleFormState>(field: K) =>
      (value: ScaleFormState[K]) => {
        updateForm((prev) => ({
          ...prev,
          [field]: value,
        }));
      },
    [updateForm],
  );

  const sidecarFacilityId = scale?.sidecarDetail?.facilityId;
  const $facilityCameras = React.useMemo(
    () =>
      camerasListQueryAtom(() => ({
        variables: { facilityId: sidecarFacilityId ?? undefined },
      })),
    [sidecarFacilityId],
  );
  const { data: camerasData } = useAtomValue($facilityCameras);
  const cameraOptions = React.useMemo(
    () =>
      camerasData?.cameras?.map((c) => ({ key: c.id, label: c.name })) ?? [],
    [camerasData],
  );

  const isTCP = form.connectionType === enumScaleConnectionType.TCP;

  const connectionTypeOptions = React.useMemo(
    () => [
      { key: enumScaleConnectionType.TCP, label: 'TCP' },
      { key: enumScaleConnectionType.Serial, label: 'Serial' },
    ],
    [],
  );

  const measurementModeOptions = React.useMemo(
    () => [
      { key: enumScaleMeasurementMode.Continuous, label: 'Continuous' },
      { key: enumScaleMeasurementMode.OnDemand, label: 'On Demand' },
    ],
    [],
  );

  const formatTypeOptions = React.useMemo(
    () =>
      Object.values(enumScaleFormatType).map((v) => ({
        key: v,
        label: v,
      })),
    [],
  );

  const unitOptions = React.useMemo(
    () =>
      DEFAULT_WEIGHT_UNITS.map((u) => ({
        key: u.unit,
        label: `${u.name} (${u.symbol})`,
      })),
    [],
  );

  return (
    <div className={cn('@ScaleFormFields', className)}>
      <ContentSection
        title={t.hardwareView.scaleDetailView.sections.configuration}
        variant="panel"
      >
        <div className="grid gap-4 md:grid-cols-2">
          <TextField
            label={t.hardwareView.scaleDetailView.fields.name}
            value={form.name}
            onChange={(value) => handleFieldChange('name')(value)}
            isRequired
          />
          <Select
            label={t.hardwareView.scaleDetailView.fields.connectionType}
            value={form.connectionType}
            onChange={(v) =>
              v && handleFieldChange('connectionType')(v as ScaleConnectionType)
            }
            options={connectionTypeOptions}
          />
          <Select
            label={t.hardwareView.scaleDetailView.fields.scaleFormatType}
            value={form.scaleFormatType}
            onChange={(v) => v && handleFieldChange('scaleFormatType')(v)}
            options={formatTypeOptions}
          />
          <Select
            label={t.hardwareView.scaleDetailView.fields.measurementMode}
            value={form.measurementMode}
            onChange={(v) =>
              v &&
              handleFieldChange('measurementMode')(v as ScaleMeasurementMode)
            }
            options={measurementModeOptions}
          />
          {form.measurementMode === enumScaleMeasurementMode.OnDemand && (
            <TextField
              label={t.hardwareView.scaleDetailView.fields.onDemandString}
              value={form.onDemandString}
              onChange={(value) => handleFieldChange('onDemandString')(value)}
            />
          )}
          <Select
            label={t.hardwareView.scaleDetailView.fields.unitOfMeasurement}
            value={form.unitOfMeasurement || null}
            onChange={(v) => handleFieldChange('unitOfMeasurement')(v ?? '')}
            options={unitOptions}
            placeholder={
              t.hardwareView.scaleCreateDialog.fields
                .unitOfMeasurementPlaceholder
            }
          />
        </div>
      </ContentSection>

      <ContentSection
        title={t.hardwareView.scaleDetailView.sections.connectionSettings}
        variant="panel"
      >
        {isTCP ? (
          <div className="grid gap-4 md:grid-cols-2">
            <TextField
              label={t.hardwareView.scaleDetailView.fields.tcpHost}
              value={form.tcpHost}
              onChange={(value) => handleFieldChange('tcpHost')(value)}
            />
            <TextField
              label={t.hardwareView.scaleDetailView.fields.tcpPort}
              value={form.tcpPort}
              onChange={(value) => handleFieldChange('tcpPort')(value)}
              inputMode="numeric"
            />
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            <TextField
              label={t.hardwareView.scaleDetailView.fields.serialPortPath}
              value={form.serialPortPath}
              onChange={(value) => handleFieldChange('serialPortPath')(value)}
            />
            <TextField
              label={t.hardwareView.scaleDetailView.fields.baudRate}
              value={form.baudRate}
              onChange={(value) => handleFieldChange('baudRate')(value)}
              inputMode="numeric"
            />
            <TextField
              label={t.hardwareView.scaleDetailView.fields.dataBits}
              value={form.dataBits}
              onChange={(value) => handleFieldChange('dataBits')(value)}
              inputMode="numeric"
            />
            <TextField
              label={t.hardwareView.scaleDetailView.fields.stopBits}
              value={form.stopBits}
              onChange={(value) => handleFieldChange('stopBits')(value)}
            />
            <TextField
              label={t.hardwareView.scaleDetailView.fields.parity}
              value={form.parity}
              onChange={(value) => handleFieldChange('parity')(value)}
            />
          </div>
        )}
      </ContentSection>

      <ContentSection
        title={t.hardwareView.scaleDetailView.sections.associatedCamera}
        variant="panel"
      >
        <div className="grid gap-4 md:grid-cols-2">
          <Select
            label={t.hardwareView.scaleDetailView.fields.camera}
            value={form.cameraId ?? ''}
            onChange={(v) => handleFieldChange('cameraId')(v || null)}
            options={[
              {
                key: '',
                label: t.hardwareView.scaleDetailView.fields.noCamera,
              },
              ...cameraOptions,
            ]}
          />
        </div>
      </ContentSection>

      <ContentSection
        title={t.hardwareView.scaleDetailView.sections.connection}
        variant="panel"
      >
        <div className="flex flex-col gap-1">
          <span className="text-sm text-neutral/60">
            {t.hardwareView.scaleDetailView.fields.sidecar}
          </span>
          {scale?.sidecarDetail ? (
            <button
              className="w-fit text-sm font-medium text-primary hover:underline"
              onClick={() =>
                setLocation(`/hardware/sidecars/${scale.sidecarDetail?.id}`)
              }
            >
              {scale.sidecarDetail.name}
            </button>
          ) : (
            <span className="text-sm font-medium">{'\u2013'}</span>
          )}
        </div>
      </ContentSection>

      <ContentSection
        title={t.hardwareView.scaleDetailView.sections.timestamps}
        variant="panel"
        hideDivider
      >
        <div className="grid gap-4 md:grid-cols-2">
          {scale?.lastSeenAt && (
            <div className="flex flex-col gap-1">
              <span className="text-sm text-neutral/60">
                {t.hardwareView.scaleDetailView.fields.lastSeenAt}
              </span>
              <span className="text-sm font-medium">
                <LocalizedDate date={scale.lastSeenAt} />
              </span>
            </div>
          )}
          <div className="flex flex-col gap-1">
            <span className="text-sm text-neutral/60">
              {t.hardwareView.scaleDetailView.fields.created}
            </span>
            <span className="text-sm font-medium">
              {scale?.timestamps.createdAt ? (
                <LocalizedDate date={scale.timestamps.createdAt} />
              ) : (
                '-'
              )}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm text-neutral/60">
              {t.hardwareView.scaleDetailView.fields.updated}
            </span>
            <span className="text-sm font-medium">
              {scale?.timestamps.updatedAt ? (
                <LocalizedDate date={scale.timestamps.updatedAt} />
              ) : (
                '-'
              )}
            </span>
          </div>
        </div>
      </ContentSection>
    </div>
  );
};
